import { FiClock, FiMinus, FiPlus, FiRotateCcw } from 'react-icons/fi';
import { useUpdateDriverNextAvailableAt } from '../../hooks/driver-operations/useDriverMutations';
import { useDriverStore, useUserStore } from '../../stores';
import Timer from '../shared/Timer';

/**
 * Modern availability timer with cleaner controls
 */
function AvailabilityTimer() {
  const { userId } = useUserStore();
  const { getDriverAvailability, getDriverRiderQueue } = useDriverStore();
  const { time: nextAvailableTime } = getDriverAvailability();
  const { riderQueue } = getDriverRiderQueue();

  const { updateDriverNextAvailableAt } = useUpdateDriverNextAvailableAt();

  const handleTimeChange = async (minutes) => {
    try {
      await updateDriverNextAvailableAt({
        variables: {
          id: userId,
          riderQueueChange: 0,
          secondsToAdd: minutes * 60,
        },
      });
    } catch (error) {
      // Handle error silently
    }
  };

  return (
    <div className="flex flex-col items-center gap-3 w-full">
      {/* Main content row - timer and queue side by side on larger screens */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 w-full">
        {/* Timer display */}
        <div className="flex flex-col items-center">
          <div className="flex items-center gap-2 text-main-light-highlight text-sm mb-2">
            <FiClock size={14} />
            <span>Next Available In</span>
          </div>
          <div className="bg-main-blue-dark/50 rounded-xl px-6 py-3 border border-main-light-highlight/30">
            <Timer
              initialTime={nextAvailableTime}
              direction="down"
              style="text-3xl md:text-4xl font-mono font-bold text-white tracking-wider"
            />
          </div>
        </div>

        {/* Queue indicator */}
        <div className="flex flex-col items-center">
          <span className="text-main-light-highlight text-sm mb-2">In Queue</span>
          <div className="bg-main-blue-dark/50 rounded-xl px-5 py-3 border border-main-light-highlight/30">
            <span className="text-3xl md:text-4xl font-mono font-bold text-white">
              {riderQueue || 0}
            </span>
          </div>
        </div>
      </div>

      {/* Time controls */}
      <div className="flex items-center gap-1.5 sm:gap-2">
        {/* Decrease buttons */}
        <button
          onClick={() => handleTimeChange(-5)}
          className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 font-semibold text-sm transition-colors flex items-center justify-center"
          title="Remove 5 minutes"
        >
          -5
        </button>
        <button
          onClick={() => handleTimeChange(-1)}
          className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-colors flex items-center justify-center"
          title="Remove 1 minute"
        >
          <FiMinus size={16} />
        </button>

        {/* Reset button */}
        <button
          onClick={() => handleTimeChange(0)}
          className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 transition-colors flex items-center justify-center"
          title="Reset to now"
        >
          <FiRotateCcw size={16} />
        </button>

        {/* Increase buttons */}
        <button
          onClick={() => handleTimeChange(1)}
          className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 transition-colors flex items-center justify-center"
          title="Add 1 minute"
        >
          <FiPlus size={16} />
        </button>
        <button
          onClick={() => handleTimeChange(5)}
          className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 font-semibold text-sm transition-colors flex items-center justify-center"
          title="Add 5 minutes"
        >
          +5
        </button>
      </div>
    </div>
  );
}

export default AvailabilityTimer;
